// Routes — централизованное определение маршрутов админки.
// Приватные маршруты обёрнуты в <RequireAuth> (Sprint 11).
// Code splitting: страницы грузятся лениво через React.lazy + <Suspense>
// (см. docs/stack/07-frontend-vite.md §11). Eager остаются только первый экран
// (Login, Dashboard) и мелкие обёртки (Layout / RequireAuth / NotFound).
// Страницы экспортируются именованно, поэтому lazy маппит именованный экспорт в default.

import { type ReactElement, Suspense, lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';

import { Layout } from '@/components/Layout';
import { RequireAuth } from '@/components/RequireAuth';
import { RouteSpinner } from '@/components/RouteSpinner';
import { useCan } from '@/lib/auth-context';
import { DashboardPage } from '@/pages/Dashboard';
import { LoginPage } from '@/pages/Login';
import { NotFoundPage } from '@/pages/NotFound';

/**
 * Стартовый экран по правам: дашборд — только тем, у кого есть право
 * reports:dashboard (Manager/Boss по дефолту); остальным — расписание
 * (attendance:mark есть у всех ролей). Иначе admin/менеджер/педагог
 * приземлялись на страницу, скрытую из их меню.
 */
function HomeRedirect(): ReactElement {
  const can = useCan();
  return <Navigate to={can('reports:dashboard') ? '/dashboard' : '/schedule'} replace />;
}

/** Права раздела «Склад» (план 154): открывает любое из них. */
const GOODS_ACTIONS = ['goods:sell', 'goods:receive', 'goods:write_off', 'goods:manage'] as const;

function RequireAnyPermissionRoute({
  actions,
  children,
}: {
  actions: readonly string[];
  children: ReactElement;
}): ReactElement {
  const can = useCan();
  return actions.some((a) => can(a)) ? children : <Navigate to="/schedule" replace />;
}

/**
 * Маршрут под правом: без него — на свой домашний экран, а не на страницу,
 * скрытую из меню. Это UX, а не защита: данные закрывает сервер (у дашборда —
 * своё право на GET /reports/dashboard).
 */
function RequirePermissionRoute({
  action,
  children,
}: {
  action: string;
  children: ReactElement;
}): ReactElement {
  const can = useCan();
  return can(action) ? children : <Navigate to="/schedule" replace />;
}

const ClientsPage = lazy(() =>
  import('@/features/clients/pages/ClientsPage').then((m) => ({ default: m.ClientsPage })),
);
const SegmentsPage = lazy(() =>
  import('@/features/clients/pages/SegmentsPage').then((m) => ({ default: m.SegmentsPage })),
);
const EmployeesPage = lazy(() =>
  import('@/features/employees/pages/EmployeesPage').then((m) => ({ default: m.EmployeesPage })),
);
const AppContentPage = lazy(() =>
  import('@/features/app-content/pages/AppContentPage').then((m) => ({
    default: m.AppContentPage,
  })),
);
const SchedulePage = lazy(() =>
  import('@/features/schedule/pages/SchedulePage').then((m) => ({ default: m.SchedulePage })),
);
// «Рабочий день» (план 121 Ф2) — экран администратора смены.
const WorkdayPage = lazy(() =>
  import('@/features/schedule/pages/WorkdayPage').then((m) => ({ default: m.WorkdayPage })),
);
const MessengerPage = lazy(() =>
  import('@/features/messenger/pages/MessengerPage').then((m) => ({ default: m.MessengerPage })),
);
const NotificationsPage = lazy(() =>
  import('@/features/notifications/pages/NotificationsPage').then((m) => ({
    default: m.NotificationsPage,
  })),
);
const TasksPage = lazy(() =>
  import('@/features/tasks/pages/TasksPage').then((m) => ({ default: m.TasksPage })),
);
const SubscriptionsPage = lazy(() =>
  import('@/features/subscriptions/pages/SubscriptionsPage').then((m) => ({
    default: m.SubscriptionsPage,
  })),
);
const CashPage = lazy(() =>
  import('@/features/cash/pages/CashPage').then((m) => ({ default: m.CashPage })),
);
// Склад товаров (план 154 Ф1+Ф2).
const GoodsPage = lazy(() =>
  import('@/features/goods/pages/GoodsPage').then((m) => ({ default: m.GoodsPage })),
);
const CashCorrectionWizardPage = lazy(() =>
  import('@/features/cash/pages/CashCorrectionWizardPage').then((m) => ({
    default: m.CashCorrectionWizardPage,
  })),
);
const CashCorrectionsPage = lazy(() =>
  import('@/features/cash/pages/CashCorrectionsPage').then((m) => ({
    default: m.CashCorrectionsPage,
  })),
);
// Очередь возвратов сверх лимита (план 168).
const CashRefundsPage = lazy(() =>
  import('@/features/cash/pages/CashRefundsPage').then((m) => ({
    default: m.CashRefundsPage,
  })),
);
const DealsPage = lazy(() =>
  import('@/features/deals/pages/DealsPage').then((m) => ({ default: m.DealsPage })),
);
const AutomationPage = lazy(() =>
  import('@/features/automation/pages/AutomationPage').then((m) => ({
    default: m.AutomationPage,
  })),
);
const TrialFollowupPage = lazy(() =>
  import('@/features/trial-followup/pages/TrialFollowupPage').then((m) => ({
    default: m.TrialFollowupPage,
  })),
);
const ReportsPage = lazy(() =>
  import('@/features/reports/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const ReferencePage = lazy(() =>
  import('@/features/reference/pages/ReferencePage').then((m) => ({ default: m.ReferencePage })),
);
const ImportPage = lazy(() =>
  import('@/features/imports/pages/ImportPage').then((m) => ({ default: m.ImportPage })),
);
const ScannerPage = lazy(() =>
  import('@/features/scanner/ScannerPage').then((m) => ({ default: m.ScannerPage })),
);
const DesignSystemPage = lazy(() =>
  import('@/pages/DesignSystem').then((m) => ({ default: m.DesignSystemPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/Settings').then((m) => ({ default: m.SettingsPage })),
);
const AccessPage = lazy(() =>
  import('@/features/access/pages/AccessPage').then((m) => ({ default: m.AccessPage })),
);
const HelpPage = lazy(() =>
  import('@/features/help/pages/HelpPage').then((m) => ({ default: m.HelpPage })),
);
const LogsPage = lazy(() =>
  import('@/features/logs/pages/LogsPage').then((m) => ({ default: m.LogsPage })),
);

// Каждый ленивый route оборачиваем в <Suspense>. Граница живёт внутри Outlet Layout,
// поэтому во время загрузки чанка сайдбар/топбар остаются, спиннер — только в контенте.
function lazyRoute(element: ReactElement): ReactElement {
  return <Suspense fallback={<RouteSpinner />}>{element}</Suspense>;
}

export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomeRedirect /> },
      {
        path: 'dashboard',
        element: (
          <RequirePermissionRoute action="reports:dashboard">
            <DashboardPage />
          </RequirePermissionRoute>
        ),
      },
      { path: 'clients', element: lazyRoute(<ClientsPage />) },
      // Короткие ссылки на карточки (план 116): /clients/1234 вместо
      // /clients?client=<uuid>. Живут ВНУТРИ ветки с RequireAuth и Layout —
      // положенные рядом с 404-роутом, они дали бы голый полноэкранный экран
      // без меню и без редиректа на вход. Query-параметры продолжают работать.
      { path: 'clients/:no', element: lazyRoute(<ClientsPage />) },
      { path: 'deals/:no', element: lazyRoute(<DealsPage />) },
      { path: 'subscriptions/:no', element: lazyRoute(<SubscriptionsPage />) },
      { path: 'schedule/:no', element: lazyRoute(<SchedulePage />) },
      { path: 'messenger', element: lazyRoute(<MessengerPage />) },
      { path: 'notifications', element: lazyRoute(<NotificationsPage />) },
      { path: 'tasks', element: lazyRoute(<TasksPage />) },
      { path: 'employees', element: lazyRoute(<EmployeesPage />) },
      { path: 'segments', element: lazyRoute(<SegmentsPage />) },
      { path: 'schedule', element: lazyRoute(<SchedulePage />) },
      { path: 'workday', element: lazyRoute(<WorkdayPage />) },
      { path: 'subscriptions', element: lazyRoute(<SubscriptionsPage />) },
      { path: 'cash', element: lazyRoute(<CashPage />) },
      {
        path: 'goods',
        element: (
          <RequireAnyPermissionRoute actions={GOODS_ACTIONS}>
            {lazyRoute(<GoodsPage />)}
          </RequireAnyPermissionRoute>
        ),
      },
      // «Правки и отмены» (план 130): своё право cash:corrections_read —
      // экран показывает правки администраторов старшему и управляющему.
      { path: 'cash/corrections', element: lazyRoute(<CashCorrectionsPage />) },
      // «Исправление операции» (план 150 Ф4.1): мастер движка исправлений —
      // право cash:correct_past (управляющий и владелец), страница сама
      // отвечает «нет доступа» остальным.
      { path: 'cash/correct', element: lazyRoute(<CashCorrectionWizardPage />) },
      // «Возвраты на подтверждении» (план 168): решают управляющий и Босс —
      // то же право, что у подтверждения на сервере.
      {
        path: 'cash/refunds',
        element: (
          <RequirePermissionRoute action="refund:over_limit">
            {lazyRoute(<CashRefundsPage />)}
          </RequirePermissionRoute>
        ),
      },
      { path: 'deals', element: lazyRoute(<DealsPage />) },
      // Обзвон после пробного (план 141): раздел показывает и двигает сделки —
      // право то же, что у «Сделок».
      {
        path: 'trial-followup',
        element: (
          <RequirePermissionRoute action="deal:read">
            {lazyRoute(<TrialFollowupPage />)}
          </RequirePermissionRoute>
        ),
      },
      // Раздел «Автоматизация» (план 73): роботы на сделках и занятиях.
      { path: 'automation', element: lazyRoute(<AutomationPage />) },
      { path: 'reports', element: lazyRoute(<ReportsPage />) },
      { path: 'reference', element: lazyRoute(<ReferencePage />) },
      // Импорт базы из файла (план 85) — рядом со справочниками: это разовая
      // настроечная операция, а не ежедневная работа.
      { path: 'import', element: lazyRoute(<ImportPage />) },
      { path: 'app-content', element: lazyRoute(<AppContentPage />) },
      { path: 'design-system', element: lazyRoute(<DesignSystemPage />) },
      { path: 'settings', element: lazyRoute(<SettingsPage />) },
      { path: 'access', element: lazyRoute(<AccessPage />) },
      { path: 'logs', element: lazyRoute(<LogsPage />) },
      { path: 'scan', element: lazyRoute(<ScannerPage />) },
      // Раздел «Помощь» (план 123): обучающие уроки для новых сотрудников.
      // БЕЗ RequirePermissionRoute — гайд нужен всем ролям, включая педагога;
      // отдельные уроки внутри фильтруются по правам самого сотрудника.
      { path: 'help', element: lazyRoute(<HelpPage />) },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];
